/**
 * AIChatMessages - Shared message list component for AI chat panels
 *
 * Renders the scrollable message list with empty state, messages, typing indicator,
 * and error display. Used by both AIChatPanel and GlobalAIChatPanel.
 */

import { forwardRef } from 'react';
import { Sparkles, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AIChatMessageBubble, {
  AIChatTypingIndicator,
  AIChatSearchingIndicator,
  AIChatError,
} from './AIChatMessageBubble';
import { ICON_SIZE } from '../../design-system';

/**
 * Empty state component - shown when no messages exist
 *
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {string} props.title - Main title text
 * @param {string} props.hint - Secondary hint text
 */
export const AIChatEmptyState = ({ icon, title, hint }) => (
  <div className="ai-chat-empty">
    {icon}
    <p>{title}</p>
    {hint && <p className="ai-chat-hint">{hint}</p>}
  </div>
);

/**
 * Default empty states for different chat types
 * Note: These are factory functions that accept t function for i18n
 */
// eslint-disable-next-line react-refresh/only-export-components -- factory/helper exported alongside its only consumer component
export const createEmptyStates = (t) => ({
  article: {
    icon: <Sparkles size={ICON_SIZE.xl} />,
    title: t('aiChat.emptyState.article.title'),
    hint: t('aiChat.emptyState.article.hint'),
  },
  rag: (scopeDisplayName) => ({
    icon: <Search size={ICON_SIZE.xl} />,
    title: t('aiChat.emptyState.rag.title', { scope: scopeDisplayName?.toLowerCase() || t('aiChat.emptyState.rag.yourArticles') }),
    hint: t('aiChat.emptyState.rag.hint'),
  }),
});

/**
 * AIChatMessages - The scrollable message list container
 *
 * @param {Object} props
 * @param {Array} props.messages - Array of message objects
 * @param {boolean} props.sending - Whether a message is being sent
 * @param {boolean} props.searching - Whether articles are being searched (RAG only)
 * @param {string} props.error - Error message to display
 * @param {function} props.onDeleteMessage - Callback when a message is deleted
 * @param {function} props.renderMessage - Custom render function for each message
 * @param {Object} props.emptyState - Empty state { icon, title, hint } or null
 */
const AIChatMessages = forwardRef(({
  messages = [],
  sending = false,
  searching = false,
  error = null,
  onDeleteMessage,
  renderMessage,
  emptyState,
}, ref) => {
  const { t } = useTranslation('reader');

  // Use provided emptyState or default to article empty state with i18n
  const resolvedEmptyState = emptyState || createEmptyStates(t).article;

  return (
    <div className="ai-chat-messages">
      {messages.length === 0 ? (
        <AIChatEmptyState
          icon={resolvedEmptyState.icon}
          title={resolvedEmptyState.title}
          hint={resolvedEmptyState.hint}
        />
      ) : (
        messages.map((msg, idx) => {
          // Use custom render if provided
          if (renderMessage) {
            return renderMessage(msg, idx);
          }

          // Default rendering
          return (
            <AIChatMessageBubble
              key={idx}
              message={msg}
              index={idx}
              onDelete={onDeleteMessage}
            >
              <span>{msg.content}</span>
            </AIChatMessageBubble>
          );
        })
      )}

      {/* Searching indicator (RAG only) */}
      {searching && <AIChatSearchingIndicator />}

      {/* Typing indicator */}
      {sending && !searching && <AIChatTypingIndicator />}

      {/* Error display */}
      <AIChatError error={error} />

      {/* Scroll anchor */}
      <div ref={ref} />
    </div>
  );
});

AIChatMessages.displayName = 'AIChatMessages';

export default AIChatMessages;
